// Pure rules behind the order template editor (no imports, so Node's test runner loads it as is).
// Messages returned here are English i18n keys; the page translates them.

export interface UnitOption {
  id: string;
  name: string;
  isBase: boolean;
}

export interface TemplateLine {
  // Stable identity on screen: from the saved detail, or made when the line was added.
  key: string;
  productId: string;
  productName: string;
  sku: string;
  groupName: string;
  units: UnitOption[];
  productUOMId: string;
  // Quantities are kept as typed; an empty Max means no limit.
  lowQty: string;
  medQty: string;
  highQty: string;
  // The product was deleted after it was added to the template.
  missing?: boolean;
}

export interface TemplateHeader {
  templateName: string;
  locationId: string;
  description: string;
  validFrom: string | null;
  validTo: string | null;
}

export type QtyField = "lowQty" | "medQty" | "highQty";
export type LineField = QtyField | "productUOMId";
export type LineErrors = Partial<Record<LineField, string>>;
export type HeaderErrors = Partial<Record<"templateName" | "locationId" | "validTo", string>>;

export const MSG = {
  unitRequired: "Choose an order unit",
  unitTaken: "Already on this template in this unit",
  notNumber: "Enter a number",
  negative: "Can't be negative",
  orderQtyRequired: "Enter the order quantity",
  orderQtyPositive: "Order qty must be more than 0",
  maxBelowOrder: "Max must be at least the order quantity, or empty for no limit",
  nameRequired: "Template name is required",
  siteRequired: "Choose a site",
  validToBeforeFrom: "Valid to can't be before valid from",
} as const;

export type Qty =
  | { kind: "empty" }
  | { kind: "invalid" }
  | { kind: "negative" }
  | { kind: "ok"; value: number };

const NUMBER = /^(\d+([.,]\d*)?|[.,]\d+)$/;

/** Reads a typed quantity; a comma is a decimal separator (phones in AL show one). */
export const parseQty = (text: unknown): Qty => {
  const raw = String(text ?? "").trim();
  if (raw === "") return { kind: "empty" };
  if (raw.startsWith("-") && NUMBER.test(raw.slice(1).trim())) return { kind: "negative" };
  if (!NUMBER.test(raw)) return { kind: "invalid" };
  const value = Number(raw.replace(",", "."));
  return Number.isFinite(value) ? { kind: "ok", value } : { kind: "invalid" };
};

const basicError = (qty: Qty): string | undefined =>
  qty.kind === "invalid" ? MSG.notNumber : qty.kind === "negative" ? MSG.negative : undefined;

/** A template line is a product in one order unit. */
export const lineKey = (productId: string, productUOMId: string) => `${productId}|${productUOMId}`;
const keyOf = (line: TemplateLine) => lineKey(line.productId, line.productUOMId);

/** Keys of lines whose product and unit another line has too. */
export const duplicateLines = (lines: TemplateLine[]): Set<string> => {
  const counts = new Map<string, number>();
  for (const line of lines) counts.set(keyOf(line), (counts.get(keyOf(line)) ?? 0) + 1);
  return new Set(lines.filter((line) => line.productUOMId && (counts.get(keyOf(line)) ?? 0) > 1).map((line) => line.key));
};

/** Unit ids each product already has on the template, saved or not. */
export const unitsOnTemplate = (lines: TemplateLine[]): Map<string, Set<string>> => {
  const map = new Map<string, Set<string>>();
  for (const line of lines) {
    if (!map.has(line.productId)) map.set(line.productId, new Set());
    map.get(line.productId)!.add(line.productUOMId);
  }
  return map;
};

export const lineErrors = (line: TemplateLine, duplicate = false): LineErrors => {
  const errors: LineErrors = {};
  if (!line.productUOMId) errors.productUOMId = MSG.unitRequired;
  else if (duplicate) errors.productUOMId = MSG.unitTaken;
  const low = parseQty(line.lowQty);
  const med = parseQty(line.medQty);
  const high = parseQty(line.highQty);
  const lowError = basicError(low);
  if (lowError) errors.lowQty = lowError;
  if (med.kind === "empty") errors.medQty = MSG.orderQtyRequired;
  else if (basicError(med)) errors.medQty = basicError(med);
  else if (med.kind === "ok" && med.value <= 0) errors.medQty = MSG.orderQtyPositive;
  const highError = basicError(high);
  if (highError) errors.highQty = highError;
  else if (high.kind === "ok" && high.value > 0 && med.kind === "ok" && med.value > 0 && high.value < med.value) {
    errors.highQty = MSG.maxBelowOrder;
  }
  return errors;
};

export const isLineValid = (line: TemplateLine, duplicate = false) => Object.keys(lineErrors(line, duplicate)).length === 0;

/** New lines arrive without an Order qty; a failed Save marks the empty box instead of showing text. */
export const missingOrderQty = (line: TemplateLine) => parseQty(line.medQty).kind === "empty";

/** The error text shown under a line: an empty Order qty has none (Save marks its box). */
export const visibleLineErrors = (line: TemplateLine, duplicate = false): LineErrors => {
  const errors = lineErrors(line, duplicate);
  if (errors.medQty === MSG.orderQtyRequired) delete errors.medQty;
  return errors;
};

export const headerErrors = (header: TemplateHeader): HeaderErrors => {
  const errors: HeaderErrors = {};
  if (!header.templateName.trim()) errors.templateName = MSG.nameRequired;
  if (!header.locationId) errors.locationId = MSG.siteRequired;
  const from = dayOf(header.validFrom);
  const to = dayOf(header.validTo);
  if (from && to && to < from) errors.validTo = MSG.validToBeforeFrom;
  return errors;
};

/* ---------------- Dates (kept as the API's ISO strings, shown as local days) ---------------- */

const pad = (n: number) => String(n).padStart(2, "0");

/** "2026-10-22" for a timestamp, in the browser's zone; "" when empty or unreadable. */
export const dayOf = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/** Local midnight of a "yyyy-mm-dd" day as an ISO string; null for an empty day. */
export const dayToIso = (day: string): string | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day ?? "");
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).toISOString();
};

/** A picked day; picking the saved day again keeps the saved timestamp, so nothing changes. */
export const pickDay = (day: string, savedIso: string | null): string | null =>
  day && day === dayOf(savedIso) ? savedIso : dayToIso(day);

/* ---------------- API <-> lines ---------------- */

const qtyText = (value: unknown, zeroIsEmpty: boolean): string => {
  if (value === null || value === undefined || String(value).trim() === "") return "";
  const number = Number(value);
  if (!Number.isFinite(number)) return "";
  if (zeroIsEmpty && number === 0) return "";
  return String(number);
};

export const unitsOf = (product: any): UnitOption[] =>
  (Array.isArray(product?.productUOMs) ? product.productUOMs : [])
    .filter((unit: any) => unit && unit.id)
    .map((unit: any) => ({ id: String(unit.id), name: String(unit.uom?.uomName ?? ""), isBase: !!unit.isBase }));

/** The line's own unit when the product still has it, else the base unit. */
export const defaultUnitId = (units: UnitOption[], preferred?: string | null): string => {
  if (!units.length) return preferred ? String(preferred) : "";
  if (preferred && units.some((unit) => unit.id === preferred)) return String(preferred);
  return units.find((unit) => unit.isBase)?.id ?? "";
};

// Keys end up in DOM ids, so they keep to letters, digits, "-" and "_".
const domSafe = (text: string) => text.replace(/[^A-Za-z0-9_-]/g, "_");

/** One template detail from GET /order-templates/:id; Max 0 and Order qty 0 show as empty. */
export const detailToLine = (detail: any): TemplateLine => {
  const product = detail?.product;
  const units = unitsOf(product);
  const productId = String(detail?.productId ?? product?.id ?? "");
  const productUOMId = defaultUnitId(units, detail?.productUOMId);
  return {
    key: detail?.id ? `s-${domSafe(String(detail.id))}` : `s-${domSafe(productId)}-${domSafe(productUOMId)}`,
    productId,
    productName: String(product?.productName ?? ""),
    sku: String(product?.sku ?? ""),
    groupName: String(product?.productGroup?.groupName ?? ""),
    units,
    productUOMId,
    lowQty: qtyText(detail?.lowQty, false),
    medQty: qtyText(detail?.medQty, true),
    highQty: qtyText(detail?.highQty, true),
    ...(product ? {} : { missing: true }),
  };
};

// A key not used yet: the base key, else the base key with a number.
const freeKey = (base: string, taken: Set<string>) => {
  let key = base;
  for (let n = 2; taken.has(key); n++) key = `${base}-${n}`;
  return key;
};

/** Lines are matched by product and unit, so a duplicate detail keeps only its first row. */
export const detailsToLines = (details: unknown): TemplateLine[] => {
  const seen = new Set<string>();
  const keys = new Set<string>();
  const lines: TemplateLine[] = [];
  for (const detail of Array.isArray(details) ? details : []) {
    const line = detailToLine(detail);
    if (!line.productId || seen.has(keyOf(line))) continue;
    seen.add(keyOf(line));
    const key = freeKey(line.key, keys);
    keys.add(key);
    lines.push({ ...line, key });
  }
  return lines;
};

/** A product picked in the Add products dialog: no quantities yet. */
export const productToLine = (product: any, unitId?: string): TemplateLine => {
  const units = unitsOf(product);
  const productId = String(product?.id ?? "");
  const productUOMId = defaultUnitId(units, unitId);
  return {
    key: `n-${domSafe(productId)}-${domSafe(productUOMId)}`,
    productId,
    productName: String(product?.productName ?? ""),
    sku: String(product?.sku ?? ""),
    groupName: String(product?.productGroup?.groupName ?? ""),
    units,
    productUOMId,
    lowQty: "",
    medQty: "",
    highQty: "",
  };
};

/** Appends picks whose product and unit are not in the template yet. */
export const addProducts = (lines: TemplateLine[], picks: { product: any; unitId?: string }[]): TemplateLine[] => {
  const seen = new Set(lines.map(keyOf));
  const keys = new Set(lines.map((line) => line.key));
  const added: TemplateLine[] = [];
  for (const pick of picks) {
    const line = productToLine(pick.product, pick.unitId);
    if (!line.productId || seen.has(keyOf(line))) continue;
    seen.add(keyOf(line));
    const key = freeKey(line.key, keys);
    keys.add(key);
    added.push({ ...line, key });
  }
  return [...lines, ...added];
};

export const templateToHeader = (template: any): TemplateHeader => ({
  templateName: String(template?.templateName ?? ""),
  locationId: String(template?.locationId ?? ""),
  description: String(template?.description ?? ""),
  validFrom: template?.validFrom ? String(template.validFrom) : null,
  validTo: template?.validTo ? String(template.validTo) : null,
});

export const newHeader = (locationId = "", validFrom: string | null = null): TemplateHeader => ({
  templateName: "",
  locationId,
  description: "",
  validFrom,
  validTo: null,
});

/** Filter over the loaded lines by product name or SKU. */
export const filterLines = (lines: TemplateLine[], query: string): TemplateLine[] => {
  const term = query.trim().toLocaleLowerCase();
  if (!term) return lines;
  return lines.filter(
    (line) => line.productName.toLocaleLowerCase().includes(term) || line.sku.toLocaleLowerCase().includes(term),
  );
};

/* ---------------- Payload ---------------- */

const qtyNumber = (text: string): number => {
  const qty = parseQty(text);
  return qty.kind === "ok" ? qty.value : 0;
};

export interface DetailPayload {
  productId: string;
  productUOMId?: string;
  lowQty: number;
  medQty: number;
  highQty: number;
}

export interface TemplatePayload {
  templateName: string;
  locationId: string;
  description: string;
  validFrom: string | null;
  validTo: string | null;
  orderTemplateDetails: DetailPayload[];
}

/** Empty Reorder at and empty Max (no limit) are sent as 0. */
export const lineToDetail = (line: TemplateLine): DetailPayload => ({
  productId: line.productId,
  ...(line.productUOMId ? { productUOMId: line.productUOMId } : {}),
  lowQty: qtyNumber(line.lowQty),
  medQty: qtyNumber(line.medQty),
  highQty: qtyNumber(line.highQty),
});

/** The whole template; the server sets the company, so it is never sent. */
export const buildPayload = (header: TemplateHeader, lines: TemplateLine[]): TemplatePayload => ({
  templateName: header.templateName.trim(),
  locationId: header.locationId,
  description: header.description.trim(),
  validFrom: header.validFrom || null,
  validTo: header.validTo || null,
  orderTemplateDetails: lines.map(lineToDetail),
});

/* ---------------- Unsaved changes ---------------- */

export type HeaderField = keyof TemplateHeader;

export type Change =
  | { kind: "field"; field: HeaderField }
  | { kind: "added" | "removed" | "changed"; key: string; productId: string; name: string };

export interface TemplateState {
  header: TemplateHeader;
  lines: TemplateLine[];
}

const sameQty = (a: string, b: string, zeroIsEmpty: boolean) => {
  const norm = (text: string) => {
    const qty = parseQty(text);
    if (qty.kind === "empty") return "";
    if (qty.kind !== "ok") return String(text).trim();
    return zeroIsEmpty && qty.value === 0 ? "" : String(qty.value);
  };
  return norm(a) === norm(b);
};

export const sameLine = (a: TemplateLine, b: TemplateLine) =>
  a.productUOMId === b.productUOMId &&
  sameQty(a.lowQty, b.lowQty, false) &&
  sameQty(a.medQty, b.medQty, false) &&
  sameQty(a.highQty, b.highQty, true);

const HEADER_FIELDS: HeaderField[] = ["templateName", "locationId", "description", "validFrom", "validTo"];

const sameField = (field: HeaderField, a: TemplateHeader, b: TemplateHeader) => {
  if (field === "validFrom" || field === "validTo") return dayOf(a[field]) === dayOf(b[field]);
  return String(a[field] ?? "").trim() === String(b[field] ?? "").trim();
};

/** Every unsaved change, header fields first, then lines in template order, then removed lines. */
export const listChanges = (saved: TemplateState, current: TemplateState): Change[] => {
  const changes: Change[] = HEADER_FIELDS.filter((field) => !sameField(field, saved.header, current.header)).map(
    (field) => ({ kind: "field" as const, field }),
  );
  const before = new Map(saved.lines.map((line) => [line.key, line]));
  const now = new Set(current.lines.map((line) => line.key));
  const change = (kind: "added" | "removed" | "changed", line: TemplateLine) =>
    changes.push({ kind, key: line.key, productId: line.productId, name: line.productName });
  for (const line of current.lines) {
    const old = before.get(line.key);
    if (!old) change("added", line);
    else if (!sameLine(old, line)) change("changed", line);
  }
  for (const line of saved.lines) {
    if (!now.has(line.key)) change("removed", line);
  }
  return changes;
};

export const invalidLineCount = (lines: TemplateLine[]) => {
  const duplicates = duplicateLines(lines);
  return lines.filter((line) => !isLineValid(line, duplicates.has(line.key))).length;
};

/** Save needs a change, a valid header and every line valid. */
export const canSave = (saved: TemplateState, current: TemplateState) =>
  listChanges(saved, current).length > 0 &&
  Object.keys(headerErrors(current.header)).length === 0 &&
  invalidLineCount(current.lines) === 0;

export type Problem =
  | { kind: "header"; field: keyof HeaderErrors }
  | { kind: "line"; key: string; field: LineField };

const HEADER_ORDER: (keyof HeaderErrors)[] = ["templateName", "locationId", "validTo"];
const LINE_ORDER: LineField[] = ["productUOMId", "lowQty", "medQty", "highQty"];

/** The first problem in page order (header, then lines top to bottom), which a failed Save focuses. */
export const firstProblem = (header: TemplateHeader, lines: TemplateLine[]): Problem | null => {
  const headerProblems = headerErrors(header);
  const headerField = HEADER_ORDER.find((field) => headerProblems[field]);
  if (headerField) return { kind: "header", field: headerField };
  const duplicates = duplicateLines(lines);
  for (const line of lines) {
    const errors = lineErrors(line, duplicates.has(line.key));
    const field = LINE_ORDER.find((name) => errors[name]);
    if (field) return { kind: "line", key: line.key, field };
  }
  return null;
};
